// pkg144 — TN stepparent adoption intake (adoption_stepparent built-in, D-A1–A6): five-section client wizard (Parties / Child / Address History / Other Biological Parent / Legal Screening); bio_parent_is drives role labels; ICWA + military + property + custody screeners with conditional detail fields; admin prop = Client Modules workspace mode (adminApi I/O, Submit hidden per D-124e) — delivered 2026-08-11
import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { adminApi } from '../utils/adminApi';
import { useFirmSettings } from '../contexts/FirmSettingsContext';

const NAVY = '#0F1838';

const YN = ['Yes', 'No'];
const YNU = ['Yes', 'No', 'Unknown'];

const EMPTY = {
  // 1 — Parties
  bio_parent_is: 'mother',        // 'mother' | 'father'
  bio_parent_name: '', bio_parent_age: '',
  stepparent_name: '', stepparent_age: '',
  marriage_date: '', marriage_county: '', marriage_state: 'Tennessee',
  // 2 — The child
  child_name: '', child_dob: '', child_sex: '',
  child_birth_city: '', child_birth_county: '', child_birth_state: 'Tennessee',
  child_address: '', residence_county: '',
  custody_since: '', child_new_name: '',
  // 3 — Address history
  address_history: '',
  // 4 — Other biological parent
  respondent_name: '', respondent_address: '', respondent_tn_resident: '',
  mother_maiden_name: '',
  military_status: '', military_branch: '', military_station: '',
  // 5 — Legal screening
  custody_proceedings: '', custody_proceedings_detail: '',
  other_custody_claims: '', other_custody_claims_detail: '',
  child_owns_property: '', child_property_detail: '',
  icwa: '', brought_to_tn: '',
};

const SECTIONS = [
  'Parties',
  'The Child',
  'Address History',
  'Other Biological Parent',
  'Legal Screening',
];

function Field({ label, children, hint }) {
  return (
    <div className="form-group">
      <label>{label}</label>
      {children}
      {hint && (
        <div style={{ fontSize: 11, color: '#888', fontFamily: 'Arial, sans-serif', marginTop: 4 }}>
          {hint}
        </div>
      )}
    </div>
  );
}
function Text({ value, onChange, placeholder, type = 'text' }) {
  return <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder || ''} />;
}
function Select({ value, onChange, options, placeholder = '— Select —' }) {
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
function Area({ value, onChange, placeholder, rows = 3 }) {
  return <textarea rows={rows} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder || ''} />;
}

// A yes/no screener that expands a detail field on "Yes".
function Screener({ label, value, onChange, detailLabel, detailValue, onDetail, options = YN }) {
  return (
    <div style={{ border: '1px solid #e0e0e0', borderRadius: 6, padding: '10px 12px', marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#333', flex: 1, minWidth: 220 }}>{label}</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {options.map(o => (
            <button key={o} type="button" onClick={() => onChange(o)}
              style={{
                fontSize: 12, padding: '4px 14px', borderRadius: 12, cursor: 'pointer',
                border: value === o ? `1px solid ${NAVY}` : '1px solid #ccc',
                background: value === o ? NAVY : '#fff',
                color: value === o ? '#fff' : '#666',
                fontFamily: 'Arial, sans-serif',
              }}>
              {o}
            </button>
          ))}
        </div>
      </div>
      {value === 'Yes' && detailLabel && (
        <div className="form-group" style={{ marginTop: 10, marginBottom: 0 }}>
          <label>{detailLabel}</label>
          <Area value={detailValue} onChange={onDetail} rows={2} />
        </div>
      )}
    </div>
  );
}

// pkg144 — `admin` prop ({ clientId }): rendered by the Client Modules
// workspace with adminApi I/O; Submit hidden (D-124e) — submission remains
// the client's act.
export default function AdoptionIntakePage({ admin = null }) {
  const { firm } = useFirmSettings();
  const io = admin ? {
    get:    () => adminApi.getClientAdoptionIntake(admin.clientId),
    save:   (data) => adminApi.saveClientAdoptionIntake(admin.clientId, data),
    submit: null,
  } : {
    get:    () => api.getAdoptionIntake(),
    save:   (data) => api.saveAdoptionIntake(data),
    submit: (data) => api.submitAdoptionIntake(data),
  };

  const [form, setForm]       = useState(EMPTY);
  const [section, setSection] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [status, setStatus]   = useState('');
  const [error, setError]     = useState('');

  useEffect(() => {
    io.get().then(rec => {
      if (rec?.data) setForm({ ...EMPTY, ...rec.data });
      if (rec?.submitted_at) setSubmitted(true);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); setStatus(''); }

  const bioIsMother = form.bio_parent_is !== 'father';
  const bioLabel  = bioIsMother ? 'mother' : 'father';
  const stepLabel = bioIsMother ? 'stepfather' : 'stepmother';
  const respLabel = bioIsMother ? 'father' : 'mother';

  async function handleSave({ silent = false } = {}) {
    setSaving(true); if (!silent) { setStatus(''); setError(''); }
    try {
      await io.save(form);
      if (!silent) setStatus('Progress saved.');
      return true;
    } catch (err) { setError(err.message); return false; }
    finally { setSaving(false); }
  }

  async function handleNext() {
    const ok = await handleSave({ silent: true });
    if (ok) { setSection(s => Math.min(s + 1, SECTIONS.length - 1)); setStatus('Progress saved.'); }
  }

  async function handleSubmit() {
    setSubmitting(true); setStatus(''); setError('');
    try {
      const res = await io.submit(form);
      setSubmitted(true);
      setStatus(res.message || 'Submitted.');
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  }

  if (loading) return <div className="loading-screen">Loading...</div>;

  return (
    <div style={{ maxWidth: 720 }}>
      <h1 className="page-title">Adoption Intake</h1>
      <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', marginBottom: 16 }}>
        Answer each section below. Your attorney uses this information to prepare the stepparent
        adoption filings. You can save your progress at any time and come back later.
      </p>

      {submitted && (
        <div className="alert alert-success" style={{ marginBottom: 16 }}>
          This intake has been submitted. You can update your answers and resubmit if anything changes.
        </div>
      )}

      {/* Section stepper */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        {SECTIONS.map((s, i) => (
          <button key={s} type="button" onClick={() => setSection(i)}
            style={{
              fontSize: 11, padding: '4px 10px', borderRadius: 12, cursor: 'pointer',
              border: 'none', fontFamily: 'Arial, sans-serif',
              background: section === i ? NAVY : '#eee',
              color: section === i ? '#fff' : '#555',
            }}>
            {i + 1} · {s}
          </button>
        ))}
      </div>

      <div className="card">
        {/* ── 1. PARTIES ─────────────────────────────────────────────────── */}
        {section === 0 && (
          <>
            <Field label="Which parent is the child's biological or legal parent?">
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {[
                  { v: 'mother', title: 'The mother', sub: 'Her spouse (stepfather) is adopting' },
                  { v: 'father', title: 'The father', sub: 'His spouse (stepmother) is adopting' },
                ].map(o => (
                  <button key={o.v} type="button" onClick={() => set('bio_parent_is', o.v)}
                    style={{
                      flex: 1, minWidth: 200, textAlign: 'left', cursor: 'pointer',
                      border: form.bio_parent_is === o.v ? `2px solid ${NAVY}` : '1px solid #ccc',
                      borderRadius: 6, padding: '10px 12px',
                      background: form.bio_parent_is === o.v ? '#F5F1E8' : '#fff',
                      fontFamily: 'Arial, sans-serif',
                    }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: form.bio_parent_is === o.v ? NAVY : '#444' }}>{o.title}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{o.sub}</div>
                  </button>
                ))}
              </div>
            </Field>
            <div className="form-row">
              <Field label={`Biological parent (${bioLabel}) — full legal name`}>
                <Text value={form.bio_parent_name} onChange={v => set('bio_parent_name', v)} />
              </Field>
              <Field label="Age">
                <Text type="number" value={form.bio_parent_age} onChange={v => set('bio_parent_age', v)} />
              </Field>
            </div>
            <div className="form-row">
              <Field label={`Stepparent adopting (${stepLabel}) — full legal name`}>
                <Text value={form.stepparent_name} onChange={v => set('stepparent_name', v)} />
              </Field>
              <Field label="Age">
                <Text type="number" value={form.stepparent_age} onChange={v => set('stepparent_age', v)} />
              </Field>
            </div>
            <div className="form-row">
              <Field label="Date of marriage"><Text type="date" value={form.marriage_date} onChange={v => set('marriage_date', v)} /></Field>
              <Field label="County of marriage"><Text value={form.marriage_county} onChange={v => set('marriage_county', v)} placeholder="e.g. Hamilton" /></Field>
              <Field label="State of marriage"><Text value={form.marriage_state} onChange={v => set('marriage_state', v)} /></Field>
            </div>
          </>
        )}

        {/* ── 2. THE CHILD ───────────────────────────────────────────────── */}
        {section === 1 && (
          <>
            <div className="form-row">
              <Field label="Child's full name (as on birth certificate)">
                <Text value={form.child_name} onChange={v => set('child_name', v)} />
              </Field>
              <Field label="Date of birth"><Text type="date" value={form.child_dob} onChange={v => set('child_dob', v)} /></Field>
            </div>
            <div className="form-row">
              <Field label="Sex"><Select value={form.child_sex} onChange={v => set('child_sex', v)} options={['Male', 'Female']} /></Field>
              <Field label="City of birth"><Text value={form.child_birth_city} onChange={v => set('child_birth_city', v)} /></Field>
            </div>
            <div className="form-row">
              <Field label="County of birth"><Text value={form.child_birth_county} onChange={v => set('child_birth_county', v)} /></Field>
              <Field label="State of birth"><Text value={form.child_birth_state} onChange={v => set('child_birth_state', v)} /></Field>
            </div>
            <Field label="Child's current home address" hint="Street, city, state, and ZIP.">
              <Text value={form.child_address} onChange={v => set('child_address', v)} />
            </Field>
            <div className="form-row">
              <Field label="County where you and the child live" hint="This is the county where the case will be filed.">
                <Text value={form.residence_county} onChange={v => set('residence_county', v)} placeholder="e.g. Hamilton" />
              </Field>
              <Field label={`Date the ${stepLabel} began living with the child`} hint="Often the marriage date.">
                <Text type="date" value={form.custody_since} onChange={v => set('custody_since', v)} />
              </Field>
            </div>
            <Field label="New name for the child after the adoption (if changing)" hint="Leave blank to keep the current name.">
              <Text value={form.child_new_name} onChange={v => set('child_new_name', v)} />
            </Field>
          </>
        )}

        {/* ── 3. ADDRESS HISTORY ─────────────────────────────────────────── */}
        {section === 2 && (
          <>
            <Field
              label="Every address where the child has lived during the last 3 years"
              hint="List each address on its own line, most recent first. Include the full street address, city, state, and ZIP."
            >
              <Area value={form.address_history} onChange={v => set('address_history', v)} rows={5}
                placeholder={'123 Main St., Hixson, TN 37343 (2023–present)\n456 Oak Ave., Chattanooga, TN 37402 (2021–2023)'} />
            </Field>
          </>
        )}

        {/* ── 4. OTHER BIOLOGICAL PARENT ─────────────────────────────────── */}
        {section === 3 && (
          <>
            <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', marginTop: 0 }}>
              These questions are about the child's biological {respLabel} — the parent whose
              rights will end so the adoption can take place.
            </p>
            <Field label={`Biological ${respLabel} — full legal name`}>
              <Text value={form.respondent_name} onChange={v => set('respondent_name', v)} />
            </Field>
            <Field label="Current address (if known)" hint="Leave blank if unknown — your attorney will discuss next steps.">
              <Text value={form.respondent_address} onChange={v => set('respondent_address', v)} />
            </Field>
            <Field label={`Does the biological ${respLabel} currently live in Tennessee?`}>
              <Select value={form.respondent_tn_resident} onChange={v => set('respondent_tn_resident', v)} options={YNU} />
            </Field>
            <Field label="Biological mother's maiden name" hint="Required for the state Putative Father Registry inquiry.">
              <Text value={form.mother_maiden_name} onChange={v => set('mother_maiden_name', v)} />
            </Field>
            <Screener
              label={`Is the biological ${respLabel} currently a member of the U.S. armed forces?`}
              value={form.military_status} onChange={v => set('military_status', v)} options={YNU}
            />
            {form.military_status === 'Yes' && (
              <div className="form-row">
                <Field label="Branch of service"><Text value={form.military_branch} onChange={v => set('military_branch', v)} /></Field>
                <Field label="Duty station (if known)"><Text value={form.military_station} onChange={v => set('military_station', v)} /></Field>
              </div>
            )}
          </>
        )}

        {/* ── 5. LEGAL SCREENING ─────────────────────────────────────────── */}
        {section === 4 && (
          <>
            <Screener
              label="Is there any custody or visitation case involving this child pending in any court, or has there ever been one?"
              value={form.custody_proceedings} onChange={v => set('custody_proceedings', v)}
              detailLabel="Tell us about it — court, county, and case number if known"
              detailValue={form.custody_proceedings_detail} onDetail={v => set('custody_proceedings_detail', v)}
            />
            <Screener
              label="Does anyone else (besides the parents) claim custody of or visitation with the child?"
              value={form.other_custody_claims} onChange={v => set('other_custody_claims', v)}
              detailLabel="Who, and what do they claim?"
              detailValue={form.other_custody_claims_detail} onDetail={v => set('other_custody_claims_detail', v)}
            />
            <Screener
              label="Does the child own any property or have an interest in any property (real estate, accounts, inheritances)?"
              value={form.child_owns_property} onChange={v => set('child_owns_property', v)}
              detailLabel="Describe the property"
              detailValue={form.child_property_detail} onDetail={v => set('child_property_detail', v)}
            />
            <Screener
              label="Is the child a member of, or eligible for membership in, a federally recognized Indian tribe?"
              value={form.icwa} onChange={v => set('icwa', v)}
            />
            {form.icwa === 'Yes' && (
              <div className="alert alert-info" style={{ marginBottom: 10 }}>
                Thank you — special federal rules apply to this adoption. Your attorney will review
                your answers and talk with you about the next steps.
              </div>
            )}
            <Screener
              label="Was the child brought into Tennessee for the purpose of foster care or adoption?"
              value={form.brought_to_tn} onChange={v => set('brought_to_tn', v)}
            />
          </>
        )}
      </div>

      {status && <div className="alert alert-success" style={{ marginTop: 12 }}>{status}</div>}
      {error && <div className="alert alert-error" style={{ marginTop: 12 }}>{error}</div>}

      <div className="btn-row" style={{ marginTop: 12 }}>
        {section > 0 && (
          <button className="btn" onClick={() => setSection(s => s - 1)}>Back</button>
        )}
        <button className="btn" onClick={() => handleSave()} disabled={saving}>
          {saving ? <><span className="spinner" />Saving...</> : 'Save Progress'}
        </button>
        {section < SECTIONS.length - 1 && (
          <button className="btn btn-primary" onClick={handleNext} disabled={saving}>
            Save and Continue
          </button>
        )}
        {section === SECTIONS.length - 1 && !admin && (
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <><span className="spinner" />Submitting...</> : (submitted ? 'Resubmit to Attorney' : 'Submit to Attorney')}
          </button>
        )}
      </div>

      {!admin && section === SECTIONS.length - 1 && (
        <p style={{ fontSize: 12, color: '#888', fontFamily: 'Arial, sans-serif', marginTop: 12 }}>
          Submitting saves your answers and notifies {firm?.client_contact_email ? 'your attorney' : 'your attorney'} that
          the intake is ready for review. You can update and resubmit at any time.
        </p>
      )}
    </div>
  );
}
